import type { ReadInputBarcodeFormat } from "zxing-wasm/reader";

/**
 * Every symbology exposed in the SDK's public UI/API, mirroring the format
 * catalogue of commercial scan SDKs. Formats flagged `supported: false` are
 * shown in the settings UI (for feature-parity with the target design) but
 * are not backed by a decoder yet — see README "Format coverage" for the
 * reasoning and how to add a custom decoder for one of them.
 */
export interface FormatDescriptor {
  /** Stable id used in the public API and persisted settings. */
  id: string;
  /** Human readable label shown in the UI. */
  label: string;
  category: "1d" | "2d";
  /** Whether a decoder currently backs this format. */
  supported: boolean;
  /** The zxing-wasm format id(s) this maps to, when supported. */
  zxing?: ReadInputBarcodeFormat[];
}

export const FORMATS: FormatDescriptor[] = [
  // ---- 1D --------------------------------------------------------------
  { id: "codabar", label: "Codabar", category: "1d", supported: true, zxing: ["Codabar"] },
  { id: "code11", label: "Code 11", category: "1d", supported: false },
  { id: "code25", label: "Code 25", category: "1d", supported: false },
  { id: "code32", label: "Code 32", category: "1d", supported: false },
  { id: "code39", label: "Code 39", category: "1d", supported: true, zxing: ["Code39"] },
  { id: "code93", label: "Code 93", category: "1d", supported: true, zxing: ["Code93"] },
  { id: "code128", label: "Code 128", category: "1d", supported: true, zxing: ["Code128"] },
  { id: "databar", label: "GS1 DataBar", category: "1d", supported: true, zxing: ["DataBar", "DataBarLimited"] },
  { id: "databar-expanded", label: "GS1 DataBar Expanded", category: "1d", supported: true, zxing: ["DataBarExpanded"] },
  { id: "ean13", label: "EAN-13", category: "1d", supported: true, zxing: ["EAN-13"] },
  { id: "ean8", label: "EAN-8", category: "1d", supported: true, zxing: ["EAN-8"] },
  { id: "iata2of5", label: "IATA 2 of 5", category: "1d", supported: false },
  { id: "industrial2of5", label: "Industrial 2 of 5", category: "1d", supported: false },
  { id: "itf", label: "ITF", category: "1d", supported: true, zxing: ["ITF"] },
  { id: "msi", label: "MSI Plessey", category: "1d", supported: false },
  { id: "upca", label: "UPC-A", category: "1d", supported: true, zxing: ["UPC-A"] },
  { id: "upce", label: "UPC-E", category: "1d", supported: true, zxing: ["UPC-E"] },
  // ---- 2D --------------------------------------------------------------
  { id: "aztec", label: "Aztec", category: "2d", supported: true, zxing: ["Aztec"] },
  { id: "datamatrix", label: "Data Matrix", category: "2d", supported: true, zxing: ["DataMatrix"] },
  { id: "maxicode", label: "MaxiCode", category: "2d", supported: true, zxing: ["MaxiCode"] },
  { id: "micropdf417", label: "Micro PDF417", category: "2d", supported: false },
  { id: "microqr", label: "Micro QR Code", category: "2d", supported: true, zxing: ["MicroQRCode"] },
  { id: "pdf417", label: "PDF417", category: "2d", supported: true, zxing: ["PDF417"] },
  { id: "qrcode", label: "QR Code", category: "2d", supported: true, zxing: ["QRCode"] },
  { id: "rmqr", label: "rMQR Code", category: "2d", supported: true, zxing: ["rMQRCode"] },
];

export const SUPPORTED_FORMAT_IDS = FORMATS.filter((f) => f.supported).map((f) => f.id);
export const ALL_FORMAT_IDS = FORMATS.map((f) => f.id);

export function formatById(id: string): FormatDescriptor | undefined {
  return FORMATS.find((f) => f.id === id);
}

/** Resolve a list of SDK format ids to the zxing-wasm formats to search for. */
export function toZxingFormats(ids: string[]): ReadInputBarcodeFormat[] {
  const set = new Set<ReadInputBarcodeFormat>();
  for (const id of ids) {
    const descriptor = formatById(id);
    if (descriptor?.supported && descriptor.zxing) {
      for (const z of descriptor.zxing) set.add(z);
    }
  }
  return [...set];
}
